import { AlertCircle, Calendar, Clock, Activity, CheckCircle, AlertTriangle, MapPin } from "lucide-react";

// ====================================================================================
// SUB-COMPONENT: GLASS CONTAINER (INLINED FOR CONSISTENCY)
// ====================================================================================
const GlassContainer = ({ title, icon: Icon, children, className = "" }) => (
    <div className={`bg-slate-900/40 border border-slate-700/50 rounded-xl relative flex flex-col ${className}`}>
        {/* Header */}
        <div className="p-4 border-b border-slate-800/50 flex items-center gap-2">
            {Icon && <Icon size={16} className="text-indigo-400" />}
            <h3 className="text-xs font-bold text-slate-200 uppercase tracking-widest">{title}</h3>
        </div>
        {/* Content */}
        <div className="p-4 flex-grow">
            {children}
        </div>
    </div>
);

export const renderLocalIssues = ({ issues }) => {
  // Sort by created_timestamp descending (newest first)
  const sortedIssues = (issues ?? []).sort((a, b) => {
    return new Date(b.created_timestamp) - new Date(a.created_timestamp);
  });

  const getPriorityStyle = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'bg-red-500/10 text-red-400 border-red-500/20';
      case 'high': return 'bg-orange-500/10 text-orange-400 border-orange-500/20';
      case 'medium': return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';
      default: return 'bg-slate-500/10 text-slate-400 border-slate-500/20';
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment?.toLowerCase()) {
      case 'negative': return <Activity size={14} className="text-red-400" />;
      case 'positive': return <CheckCircle size={14} className="text-green-400" />;
      default: return <Activity size={14} className="text-slate-400" />;
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <GlassContainer title="Local Community Intelligence" icon={AlertCircle}>
        <div className="space-y-4">
          {sortedIssues.length > 0 ? (
            sortedIssues.map((issue) => (
              <div 
                key={issue.id} 
                className="bg-slate-900/40 border border-slate-700/50 rounded-lg p-5 hover:border-slate-600 transition-colors relative group"
              >
                
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                  <div className="flex-grow">
                    <div className="flex items-center gap-3 mb-2">
                       <h4 className="text-lg font-bold text-white tracking-tight">{issue.title}</h4>
                       <span className={`px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider rounded border ${getPriorityStyle(issue.priority)}`}>
                          {issue.priority} Priority
                       </span>
                    </div>
                    
                    {/* Metadata Row */}
                    <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400">
                      <div className="flex items-center gap-1.5 bg-slate-800/50 px-2 py-1 rounded border border-slate-700/30">
                        {getSentimentIcon(issue.sentiment)}
                        <span className="capitalize text-slate-300 font-medium">{issue.sentiment} Sentiment</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Calendar size={14} className="text-slate-500" />
                        <span className="font-mono text-slate-400">{new Date(issue.date_added).toLocaleDateString()}</span>
                      </div>
                       <div className="flex items-center gap-1.5">
                        <Clock size={14} className="text-slate-500" />
                        <span className="font-mono text-slate-400">{new Date(issue.created_timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Description Box */}
                <div className="relative pl-4">
                    <div className="absolute top-0 left-0 bottom-0 w-1 bg-slate-700/50 rounded-full"></div>
                    <p className="text-sm text-slate-300 leading-relaxed">
                    {issue.description}
                    </p>
                </div>
                
                {/* ID Footer */}
                <div className="mt-4 pt-3 border-t border-slate-800/50 flex justify-between items-center">
                   <div className="flex items-center text-xs text-slate-500">
                      <MapPin size={12} className="mr-1 text-indigo-400/70" />
                      <span>Reported Location Issue</span>
                   </div>
                   <span className="text-[10px] text-slate-600 font-mono tracking-wider">REF: {issue.id}</span>
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center p-12 text-slate-500 border-2 border-dashed border-slate-700/50 rounded-xl bg-slate-900/20">
              <AlertTriangle size={32} className="mb-3 opacity-20" />
              <p className="italic text-sm">No local issues reported for this ward.</p>
            </div>
          )}
        </div>
      </GlassContainer>
    </div>
  );
};